const JOYSTICK_MAX_DISTANCE = 58;
const JOYSTICK_DEAD_ZONE = 8;

export interface MobileJoystickSnapshot {
  angle: number | null;
  active: boolean;
  distanceRatio: number;
  offsetX: number;
  offsetY: number;
}

export interface MobileControlsInputSource {
  isMobileEnabled(): boolean;
  getJoystickAngle(): number | null;
  isJoystickActive(): boolean;
  isBoostActive(): boolean;
}

export interface MobileControlsState extends MobileControlsInputSource {
  setMobileEnabled(enabled: boolean): void;
  getJoystickSnapshot(): MobileJoystickSnapshot;
}

interface InstallMobileControlsOptions {
  root: HTMLElement;
  joystick: HTMLElement;
  thumb: HTMLElement;
  boostButton: HTMLElement;
  state: MobileControlsState;
}

interface JoystickVector {
  angle: number | null;
  distanceRatio: number;
  offsetX: number;
  offsetY: number;
}

export function createMobileControlsState(): MobileControlsState {
  let mobileEnabled = false;
  let snapshot: MobileJoystickSnapshot = {
    angle: null,
    active: false,
    distanceRatio: 0,
    offsetX: 0,
    offsetY: 0,
  };
  let boostActive = false;

  return {
    setMobileEnabled(enabled) {
      mobileEnabled = enabled;
      if (!enabled) {
        boostActive = false;
      }
    },
    isMobileEnabled() {
      return mobileEnabled;
    },
    getJoystickAngle() {
      return mobileEnabled ? snapshot.angle : null;
    },
    isJoystickActive() {
      return mobileEnabled && snapshot.active;
    },
    isBoostActive() {
      return mobileEnabled && boostActive;
    },
    getJoystickSnapshot() {
      return snapshot;
    },
    updateJoystick(nextSnapshot: MobileJoystickSnapshot) {
      snapshot = nextSnapshot;
    },
    setBoostActive(active: boolean) {
      boostActive = active;
    },
  } as MobileControlsState & {
    updateJoystick(nextSnapshot: MobileJoystickSnapshot): void;
    setBoostActive(active: boolean): void;
  };
}

export function installMobileControls(
  options: InstallMobileControlsOptions,
): () => void {
  const state = options.state as MobileControlsState & {
    updateJoystick(nextSnapshot: MobileJoystickSnapshot): void;
    setBoostActive(active: boolean): void;
  };
  let joystickPointerId: number | null = null;
  let boostPointerId: number | null = null;

  const stopUiEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const updateThumb = (snapshot: MobileJoystickSnapshot) => {
    options.thumb.style.transform = `translate(${snapshot.offsetX.toFixed(1)}px, ${snapshot.offsetY.toFixed(1)}px)`;
    options.root.classList.toggle("is-joystick-active", snapshot.active);
  };

  const updateJoystickFromEvent = (
    event: PointerEvent,
    active: boolean,
  ): void => {
    const rect = options.joystick.getBoundingClientRect();
    const vector = calculateJoystickVector(
      event.clientX - (rect.left + rect.width / 2),
      event.clientY - (rect.top + rect.height / 2),
    );
    const previousAngle = state.getJoystickSnapshot().angle;
    const nextSnapshot: MobileJoystickSnapshot = {
      angle: vector.angle ?? previousAngle,
      active,
      distanceRatio: vector.distanceRatio,
      offsetX: active ? vector.offsetX : 0,
      offsetY: active ? vector.offsetY : 0,
    };
    state.updateJoystick(nextSnapshot);
    updateThumb(nextSnapshot);
  };

  const handlePointerDown = (event: PointerEvent) => {
    stopUiEvent(event);
    if (!state.isMobileEnabled() || joystickPointerId !== null) {
      return;
    }
    joystickPointerId = event.pointerId;
    options.joystick.setPointerCapture(event.pointerId);
    updateJoystickFromEvent(event, true);
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (event.pointerId !== joystickPointerId) {
      return;
    }
    stopUiEvent(event);
    updateJoystickFromEvent(event, true);
  };

  const handlePointerEnd = (event: PointerEvent) => {
    if (event.pointerId !== joystickPointerId) {
      return;
    }
    stopUiEvent(event);
    joystickPointerId = null;
    if (options.joystick.hasPointerCapture(event.pointerId)) {
      options.joystick.releasePointerCapture(event.pointerId);
    }
    const previous = state.getJoystickSnapshot();
    const nextSnapshot: MobileJoystickSnapshot = {
      ...previous,
      active: false,
      distanceRatio: 0,
      offsetX: 0,
      offsetY: 0,
    };
    state.updateJoystick(nextSnapshot);
    updateThumb(nextSnapshot);
  };

  const setBoostButtonActive = (active: boolean) => {
    state.setBoostActive(active);
    options.boostButton.classList.toggle("is-boost-active", active);
  };

  const handleBoostPointerDown = (event: PointerEvent) => {
    stopUiEvent(event);
    if (!state.isMobileEnabled() || boostPointerId !== null) {
      return;
    }
    boostPointerId = event.pointerId;
    options.boostButton.setPointerCapture(event.pointerId);
    setBoostButtonActive(true);
  };

  const handleBoostPointerEnd = (event: PointerEvent) => {
    if (event.pointerId !== boostPointerId) {
      return;
    }
    stopUiEvent(event);
    boostPointerId = null;
    if (options.boostButton.hasPointerCapture(event.pointerId)) {
      options.boostButton.releasePointerCapture(event.pointerId);
    }
    setBoostButtonActive(false);
  };

  options.root.addEventListener("pointerdown", stopUiEvent);
  options.root.addEventListener("pointermove", stopUiEvent);
  options.root.addEventListener("pointerup", stopUiEvent);
  options.root.addEventListener("pointercancel", stopUiEvent);
  options.joystick.addEventListener("pointerdown", handlePointerDown);
  options.joystick.addEventListener("pointermove", handlePointerMove);
  options.joystick.addEventListener("pointerup", handlePointerEnd);
  options.joystick.addEventListener("pointercancel", handlePointerEnd);
  options.joystick.addEventListener("lostpointercapture", handlePointerEnd);
  options.boostButton.addEventListener("pointerdown", handleBoostPointerDown);
  options.boostButton.addEventListener("pointermove", stopUiEvent);
  options.boostButton.addEventListener("pointerup", handleBoostPointerEnd);
  options.boostButton.addEventListener("pointercancel", handleBoostPointerEnd);
  options.boostButton.addEventListener(
    "lostpointercapture",
    handleBoostPointerEnd,
  );

  return () => {
    options.root.removeEventListener("pointerdown", stopUiEvent);
    options.root.removeEventListener("pointermove", stopUiEvent);
    options.root.removeEventListener("pointerup", stopUiEvent);
    options.root.removeEventListener("pointercancel", stopUiEvent);
    options.joystick.removeEventListener("pointerdown", handlePointerDown);
    options.joystick.removeEventListener("pointermove", handlePointerMove);
    options.joystick.removeEventListener("pointerup", handlePointerEnd);
    options.joystick.removeEventListener("pointercancel", handlePointerEnd);
    options.joystick.removeEventListener(
      "lostpointercapture",
      handlePointerEnd,
    );
    options.boostButton.removeEventListener(
      "pointerdown",
      handleBoostPointerDown,
    );
    options.boostButton.removeEventListener("pointermove", stopUiEvent);
    options.boostButton.removeEventListener("pointerup", handleBoostPointerEnd);
    options.boostButton.removeEventListener(
      "pointercancel",
      handleBoostPointerEnd,
    );
    options.boostButton.removeEventListener(
      "lostpointercapture",
      handleBoostPointerEnd,
    );
  };
}

export function calculateJoystickVector(
  deltaX: number,
  deltaY: number,
): JoystickVector {
  const distance = Math.hypot(deltaX, deltaY);
  if (distance < JOYSTICK_DEAD_ZONE) {
    return {
      angle: null,
      distanceRatio: 0,
      offsetX: 0,
      offsetY: 0,
    };
  }

  const clampedDistance = Math.min(distance, JOYSTICK_MAX_DISTANCE);
  const scale = clampedDistance / distance;
  return {
    angle: Math.atan2(deltaY, deltaX),
    distanceRatio: clampedDistance / JOYSTICK_MAX_DISTANCE,
    offsetX: deltaX * scale,
    offsetY: deltaY * scale,
  };
}
